import { h, Component, cloneElement } from 'preact';
import cx from 'classnames';
import { Text } from 'preact-i18n';
import { stockAPIStreamer, sortedSecurityList } from '@os/cloudquote';
import { shallowEqual, calcChange, generatePriceFormatter, percentFormatter } from '../../lib/util';
import { getGoodPrevClose } from '../../lib/util-stock-price';
import { FinanceSpinner } from '../glyphs';
import config from '../../defaults';
import { Carousel, DeviceVariance } from '@cube/blocks';
import style from './style';

export default class StockTickerBar extends Component {

	setStreamer() {
		this.streamer = stockAPIStreamer(this.props.symbols, 'getStockPrice').on('data', this.onData);
		if (!this.props.isStreaming && this.streamer) {
			this.streamer.end();
		}
	}

	onData = data => {
		if (data && data.rows) {
			this.setState({ data: data.rows });
		}
	};

	static defaultProps = {
		symbols: config.defaultSymbols
	};

	componentWillMount() {
		if (this.props.query) {
			sortedSecurityList(this.props.query)
				.then((data) => {
					if (data && data.rows) {
						this.props.symbols = data.rows.map( (row, i) => row.UniqueSymbol);
					}
					this.setStreamer();
				});
		}
		else {
			this.setStreamer();
		}
	}

	componentWillReceiveProps(nextProps) {
		// Next has query which is different than before or did not contain query before
		if (nextProps.query) {
			if (!this.props.query || !shallowEqual(nextProps.query, this.props.query)) {
				sortedSecurityList(nextProps.query)
					.then((data) => {
						if (data && data.rows) {
							this.setState({ data: null });
							nextProps.symbols = data.rows.map( (row, i) => row.UniqueSymbol);
							this.streamer.stream(nextProps.symbols);
						}
						if (!this.props.isStreaming) {
							this.streamer.end();
						}
					});
			}
		}
		// Next has different symbols
		else if (!shallowEqual(nextProps.symbols, this.props.symbols)) {
			this.setState({ data: null });
			this.streamer.stream(nextProps.symbols);
			if (!this.props.isStreaming) {
				this.streamer.end();
			}
		}
		else if (nextProps.isStreaming && !this.props.isStreaming) {
			this.streamer.stream(nextProps.symbols);
		}
		// Turn streaming off
		else if (!nextProps.isStreaming && this.props.isStreaming) {
			this.streamer.end();
	  }
	}

	componentWillUnmount() {
		if (this.streamer) {
			this.streamer.end();
		}
	}

	render({ symbols, isStreaming }, { data }) {
		if (!data || !data.length) return (
			<div class={style['stock-ticker-bar']}>
				<FinanceSpinner class={style.spinner} />
			</div>
		);

		const child = (
			<Carousel noItemsMsg={<Text id="stock-ticker-bar.noIndexesSelected">No indexes selected</Text>}>
				{data.map( q => <StockTickerBarItem q={q} />)}
			</Carousel>
		);

		return (
			<div class={cx(style['stock-ticker-bar'])}>
				<div class={style.items}>
					<DeviceVariance
						adaptive
						phone={cloneElement(child, { showScrollButtons: false })}
						other={child}
					/>
				</div>
			</div>
		);
	}
}

/**
 * Place the Symbol name in the upper left of the item
 */
export const ItemUpperLeft = ({ q }) => (
	<span class={style.symbol}>{ q.ShortName || q.Symbol }</span>
);

/**
 * Place the percent change in the upper right of the item for desktop
 */
export const ItemUpperRight = ({ q, change }) => (
	<span>
		{percentFormatter(change.percent)}
	</span>
);

/**
 * Place the value (desktop) or percent (mobile) in the lower left
 */
export const ItemLowerLeft = ({ q, change }) => <span class={style.price}>{ generatePriceFormatter(q)(q.Price) }</span>;

/**
 * Place the value change in the lower right for desktop
 */
export const ItemLowerRight = ({ q, change }) => (
	<span class={style.changePrice}>
		{generatePriceFormatter(q)(change.price, { Plus: true })}
	</span>
);

export const StockTickerBarItem = ({ q }) => {
	//Gracefully handle a bad quote Lookup
	if (!q) return null;

	let goodPrevClose = getGoodPrevClose(q);
	let change = calcChange(q.Price, goodPrevClose);

	return (
		<div class={cx(style.item, style[change.class])}>
			<a href={`/finance/category/stockdetail/symbol/${q.UniqueSymbol}`}
				data-tracking-text={q.UniqueSymbol}
			>
				<span class={style.view}>
					<span class={style.row}>
						<DeviceVariance
							phone={<ItemUpperLeft q={q} />}
							other={
								<span>
									<ItemUpperLeft q={q} />
									{' '}
									<ItemUpperRight {...{ q, change }} />
								</span>
							}
						/>
					</span>
					<span class={style.row}>
						<span class={cx(style.change, style[change.class])}>
							<DeviceVariance
								phone={<ItemUpperRight {...{ q, change }} />}
								other={
									<span>
										<ItemLowerLeft {...{ q, change }} />
										{' '}
										<ItemLowerRight {...{ q, change }} />
									</span>
								}
							/>
						</span>
					</span>
				</span>
			</a>
		</div>
	);
};
